import React, { memo, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AccountTree,
  TrendingUp,
  Star,
  Schedule,
  EmojiEvents,
  Security,
  CheckCircle,
  ArrowForward,
  SupportAgent,
  AttachMoney,
  Favorite,
  AllInclusive,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { styled } from '@mui/material/styles';
import { useThemeMode } from '../contexts/ThemeContext';
import HeroCarousel from '../components/HeroCarousel';
import { heroSlides } from '../config/heroSlides';

const MotionBox = motion(Box);

// Glass morphism card component
const GlassCard = styled(Box)<{ $isDark: boolean }>(({ theme, $isDark }) => ({
  background: $isDark 
    ? 'rgba(255, 255, 255, 0.05)' 
    : 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  borderRadius: '24px',
  border: $isDark
    ? '1px solid rgba(255, 255, 255, 0.1)'
    : '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: $isDark
    ? '0 8px 32px rgba(0, 0, 0, 0.4)'
    : '0 8px 32px rgba(31, 38, 135, 0.15)',
  padding: theme.spacing(4),
  transition: 'transform 0.3s ease, box-shadow 0.3s ease, border 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  willChange: 'transform',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: $isDark
      ? '0 12px 48px rgba(0, 0, 0, 0.5)'
      : '0 12px 48px rgba(31, 38, 135, 0.25)',
    border: $isDark
      ? '1px solid rgba(255, 255, 255, 0.2)'
      : '1px solid rgba(255, 255, 255, 0.5)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: $isDark
      ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, transparent 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, transparent 100%)',
    borderRadius: '24px',
    pointerEvents: 'none',
  }
}));

// Premium glass button
const GlassButton = styled(Button)<{ $variant?: 'primary' | 'secondary'; $isDark: boolean }>(({ theme, $variant = 'primary', $isDark }) => ({
  background: $variant === 'primary'
    ? $isDark
      ? 'rgba(255, 215, 0, 0.1)'
      : 'rgba(255, 215, 0, 0.8)'
    : 'transparent',
  backdropFilter: 'blur(5px)',
  WebkitBackdropFilter: 'blur(5px)',
  border: $variant === 'primary'
    ? $isDark
      ? '1px solid rgba(255, 215, 0, 0.3)'
      : '1px solid rgba(255, 215, 0, 0.5)'
    : $isDark
      ? '1px solid rgba(255, 255, 255, 0.2)'
      : '1px solid rgba(0, 0, 0, 0.1)',
  borderRadius: '16px',
  padding: '16px 32px',
  fontSize: '1.1rem',
  fontWeight: 600,
  color: $variant === 'primary'
    ? $isDark
      ? '#ffd700'
      : '#000'
    : $isDark
      ? '#fff'
      : '#000',
  textTransform: 'none',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    background: $variant === 'primary'
      ? $isDark
        ? 'rgba(255, 215, 0, 0.2)'
        : 'rgba(255, 215, 0, 0.9)'
      : $isDark
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.05)',
    transform: 'translateY(-2px)',
    boxShadow: $variant === 'primary'
      ? '0 8px 32px rgba(255, 215, 0, 0.3)'
      : '0 8px 32px rgba(0, 0, 0, 0.1)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: $variant === 'primary'
      ? 'radial-gradient(circle, rgba(255, 215, 0, 0.3) 0%, transparent 70%)'
      : 'radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%)',
    transform: 'scale(0)',
    transition: 'transform 0.6s ease',
    pointerEvents: 'none',
  },
  '&:hover::before': {
    transform: 'scale(1)',
  }
}));

// Feature icon container
const IconContainer = styled(Box)<{ $color: string; $isDark: boolean }>(({ $color, $isDark }) => ({
  width: '80px',
  height: '80px',
  borderRadius: '20px',
  background: $isDark
    ? `rgba(${$color}, 0.1)`
    : `rgba(${$color}, 0.15)`,
  backdropFilter: 'blur(5px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '24px',
  border: $isDark
    ? `1px solid rgba(${$color}, 0.2)`
    : `1px solid rgba(${$color}, 0.3)`,
  transition: 'all 0.3s ease',
  '& svg': {
    fontSize: '40px',
    color: `rgb(${$color})`,
  }
}));

const LandingPage: React.FC = memo(() => {
  const theme = useTheme();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const features = useMemo(() => [
    {
      icon: <AccountTree />,
      title: 'Health Progress Mapping',
      description: 'Visualize and map your health journey based upon your objectives and time available.',
      color: '74, 144, 226', // Blue
    },
    {
      icon: <TrendingUp />,
      title: 'Efficiency',
      description: '80% of the results with 20% of the time. Our health tree cuts out longevity noise and gets right to what is effective.',
      color: '255, 152, 0', // Orange
    },
    {
      icon: <AllInclusive />,
      title: 'Build Health Habits',
      description: 'With our lock-in system, you will be building lifelong health habits.',
      color: '76, 175, 80', // Green
    },
    {
      icon: <SupportAgent />,
      title: 'Health Buddy',
      description: 'We are there for you every day. Nudging you along towards your goals with daily and weekly reminders.',
      color: '156, 39, 176', // Purple
    },
    {
      icon: <AttachMoney />,
      title: 'Healthworth',
      description: 'See what your health is worth and track it with advanced analytics over time.',
      color: '0, 188, 212', // Cyan
    },
    {
      icon: <Security />,
      title: 'Secure & Private',
      description: 'Your health data is encrypted with enterprise-grade security.',
      color: '244, 67, 54', // Red
    },
  ], []);

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        position: 'relative', 
        overflow: 'hidden',
        background: isDark
          ? 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 25%, #16213e 50%, #0f3460 75%, #0a0a0a 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 25%, #cbd5e1 50%, #94a3b8 75%, #64748b 100%)',
        '&::before': {
          content: '""',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: isDark
            ? 'radial-gradient(circle at 50% 50%, rgba(120, 119, 198, 0.15) 0%, transparent 70%)'
            : 'radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
          pointerEvents: 'none',
          zIndex: 0,
        },
        '&::after': {
          content: '""',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: isDark
            ? 'radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.2) 100%)'
            : 'radial-gradient(ellipse at center, transparent 0%, rgba(255,255,255,0.1) 100%)',
          pointerEvents: 'none',
          zIndex: 0,
        }
      }}
    >
      {/* Hero Carousel */}
      <HeroCarousel slides={heroSlides} autoPlayInterval={8000} />
      
      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Container maxWidth="lg" sx={{ pt: 8, pb: 8 }}>
          {/* Trust Indicators */}
          <MotionBox
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            sx={{ mb: 8 }}
          >
            <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
              <GlassCard
                $isDark={isDark}
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderRadius: '12px',
                }}
              >
                <Star sx={{ color: '#ffd700', fontSize: '1.5rem' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  4.9/5 Rating
                </Typography>
              </GlassCard>
              <GlassCard
                $isDark={isDark}
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderRadius: '12px',
                }}
              >
                <Favorite sx={{ color: '#e91e63', fontSize: '1.5rem' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  5K+ Active Users
                </Typography>
              </GlassCard>
              <GlassCard
                $isDark={isDark}
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderRadius: '12px',
                }}
              >
                <CheckCircle sx={{ color: '#4caf50', fontSize: '1.5rem' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Scientifically Validated
                </Typography>
              </GlassCard>
            </Box>
          </MotionBox>

          {/* Features Section */}
          <Box sx={{ mb: 12 }}>
            <Typography
              variant="h2"
              component="h2"
              sx={{ 
                textAlign: 'center', 
                mb: 8, 
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
              }}
            >
              Everything You Need to Succeed
            </Typography>
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <Box sx={{ height: '100%' }}>
                    <GlassCard
                      $isDark={isDark}
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        p: 4,
                      }}
                    >
                      <IconContainer $color={feature.color} $isDark={isDark}>
                        {feature.icon}
                      </IconContainer>
                      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                        {feature.title}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                          lineHeight: 1.7,
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </GlassCard>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Pricing Preview */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h2"
              component="h2"
              sx={{ 
                mb: 8, 
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
              }}
            >
              Simple, Transparent Pricing
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              <Grid item xs={12} md={5} lg={4}>
                <Box>
                  <GlassCard
                    $isDark={isDark}
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      height: '500px',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                    }}
                  >
                    <Box sx={{ height: '29px', mb: 1 }}>{/* Spacer to match premium card badge */}</Box>
                    <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                      Free
                    </Typography>
                    <Typography 
                      variant="h2" 
                      sx={{ 
                        mb: 1, 
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #4a90e2 0%, #63b8ff 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      $0
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 4, opacity: 0.7 }}>
                      Forever free
                    </Typography>
                    <Box sx={{ mb: 4, flexGrow: 1, minHeight: '120px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                      <Chip label="3 Active Goals" sx={{ m: 0.5 }} />
                      <Chip label="Basic Talent Tree" sx={{ m: 0.5 }} />
                      <Chip label="Weekly Reports" sx={{ m: 0.5 }} />
                      <Chip label="Basic Analytics" sx={{ m: 0.5 }} />
                    </Box>
                    <Link to="/register" style={{ textDecoration: 'none', display: 'block' }}>
                      <GlassButton
                        $variant="secondary"
                        $isDark={isDark}
                        fullWidth
                      >
                        Get Started Free
                      </GlassButton>
                    </Link>
                  </GlassCard>
                </Box>
              </Grid>
              <Grid item xs={12} md={5} lg={4}>
                <Box>
                  <GlassCard
                    $isDark={isDark}
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      height: '500px',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      border: isDark
                        ? '2px solid rgba(255, 215, 0, 0.3)'
                        : '2px solid rgba(255, 215, 0, 0.5)',
                    }}
                  >
                    <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                      Premium
                    </Typography>
                    <Typography 
                      variant="h2" 
                      sx={{ 
                        mb: 1, 
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      $18.99
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 4, opacity: 0.7 }}>
                      per month
                    </Typography>
                    <Box sx={{ mb: 4, flexGrow: 1, minHeight: '120px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                      <Chip label="Everything in Free plus" sx={{ m: 0.5, backgroundColor: 'rgba(255, 215, 0, 0.2)' }} />
                      <Chip label="Unlimited Goals" sx={{ m: 0.5 }} />
                      <Chip label="All Talent Trees" sx={{ m: 0.5 }} />
                      <Chip label="Advanced Analytics" sx={{ m: 0.5 }} />
                      <Chip label="Priority Support" sx={{ m: 0.5 }} />
                    </Box>
                    <Link to="/register" style={{ textDecoration: 'none', display: 'block' }}>
                      <GlassButton
                        $variant="primary"
                        $isDark={isDark}
                        fullWidth
                      >
                        Register for Premium
                      </GlassButton>
                    </Link>
                  </GlassCard>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Final CTA */}
          <Box sx={{ mt: 12, textAlign: 'center' }}>
            <GlassCard
              $isDark={isDark}
              sx={{
                p: { xs: 4, md: 6 },
                maxWidth: '800px',
                mx: 'auto',
                background: isDark
                  ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.05) 0%, rgba(255, 255, 255, 0.05) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 255, 255, 0.8) 100%)',
              }}
            >
              <Typography variant="h3" sx={{ mb: 3, fontWeight: 700 }}>
                Ready to Invest in Your Future Health?
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 4, 
                  opacity: 0.8,
                  maxWidth: '600px',
                  mx: 'auto',
                }}
              >
                Join thousands who are actively extending their healthspan with consistent, science-backed habits
              </Typography>
              <Link to="/register" style={{ textDecoration: 'none' }}>
                <GlassButton
                  $variant="primary"
                  $isDark={isDark}
                  size="large"
                  endIcon={<EmojiEvents />}
                  sx={{ fontSize: '1.2rem', px: 5, py: 2 }}
                >
                  Start Your Health Journey
                </GlassButton>
              </Link>
            </GlassCard>
          </Box>
        </Container>
      </Box>
    </Box>
  );
});

LandingPage.displayName = 'LandingPage';

export default LandingPage;